import { MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { forwardRef, type JSX, useId } from 'react';
import { AppLink } from '@//components/AppLink';
import { type InventoryStatus, InventoryStatusBadge } from '@//containers/InventoryStatusBadge';
import type { CfitImage } from '@//utils/image/urlToCfit';
import { Badge } from '../Badge';
import styles from './ProductCard.module.scss';

const assetPrefix = process.env.NEXT_PUBLIC_ASSETS_CDN || '';

const fallbackImage: CfitImage = {
  src: `${assetPrefix}/images/placeholders/product-card.jpg`,
  srcSet: `${assetPrefix}/images/placeholders/product-card@2x.jpg 2x`,
  alt: 'Image missing',
  width: 450,
  height: 299,
};

export interface ProductCardDetailsProps {
  label: string;
  value: string;
}

export interface ProductCardPriceProps {
  label: string;
  value: string;
}

interface ProductCardProps {
  href: string;
  image: CfitImage;
  title: string;
  subTitle?: string;
  location?: string;
  retailer: string;
  details?: ProductCardDetailsProps[];
  isNew?: boolean;
  mainPrice?: ProductCardPriceProps;
  otherPrice?: ProductCardPriceProps[];
  manufacturerPackaingImage?: {
    src: string;
    srcSet?: string;
    alt: string;
  };
  inventoryStatus: InventoryStatus;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  floatingAction?: React.ReactNode;
  dominantColor?: string;
  testId: string;
  sold?: boolean;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const ProductCard = forwardRef<HTMLElement, ProductCardProps>(
  (
    {
      image,
      title,
      subTitle,
      location,
      retailer,
      href,
      details,
      isNew,
      mainPrice,
      otherPrice,
      manufacturerPackaingImage,
      inventoryStatus,
      headingLevel = 2,
      testId,
      floatingAction,
      onLinkClick,
      sold,
    },
    ref,
  ) => {
    const t = useTranslations('components.productCard');
    const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;

    const linkTitle = subTitle ? `${title} ${subTitle}` : title;
    const id = useId();
    const headingId = `${id}-heading`;
    const descriptionId = `${id}-description`;

    return (
      <article
        ref={ref}
        className={styles.card}
        data-testid={testId}
        aria-labelledby={headingId}
        aria-describedby={subTitle ? descriptionId : undefined}
      >
        <div className={styles.imageWrapper}>
          <picture>
            {image.webpSrcSet && (
              <source type="image/webp" srcSet={image.webpSrcSet} sizes={image.sizes} />
            )}
            <img
              src={image.src}
              srcSet={image.srcSet}
              sizes={image.sizes}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading={image.loading}
              className={styles.image}
              data-testid={`${testId}-image`}
              onError={(e) => {
                const target = e.currentTarget;
                target.onerror = null; // prevents infinite loop if fallback also fails
                const webpSource = target.parentElement?.querySelector('source[type="image/webp"]');
                webpSource?.remove();
                target.src = fallbackImage.src;
                target.srcset = fallbackImage.srcSet!;
                target.removeAttribute('sizes');
                target.alt = fallbackImage.alt;
                target.width = fallbackImage.width!;
                target.height = fallbackImage.height!;
              }}
            />
          </picture>
          {image.blurSrc && (
            <img
              src={image.blurSrc}
              alt=""
              width={image.width}
              height={image.height}
              loading={image.loading}
              decoding="async"
              className={styles.imageBlur}
              aria-hidden="true"
            />
          )}
        </div>
        <div className={styles.body}>
          <div className={styles.meta}>
            <dl className={styles.retailer}>
              {location && (
                <>
                  <dt className="sr-only">{t('sr.location')}</dt>
                  <dd className={styles.location}>
                    <MapPin aria-hidden="true" />
                    {location}
                  </dd>
                </>
              )}
              <dt className="sr-only">{t('sr.retailer')}</dt>
              <dd className={styles.branch}>{retailer}</dd>
            </dl>
            <ul className={styles.statuses} aria-label={t('vehicleStatus')}>
              <li className={styles.status}>
                <InventoryStatusBadge status={sold ? 'Sold' : inventoryStatus} size="sm" />
              </li>
              {isNew && (
                <li className={styles.status}>
                  <Badge size="sm" tone="brand">
                    {t('new')}
                  </Badge>
                </li>
              )}
            </ul>
          </div>
          <HeadingTag id={headingId} className={styles.title} data-testid={`${testId}-title`}>
            <AppLink
              href={href}
              title={linkTitle}
              className={styles.link}
              data-testid={`${testId}-link`}
              onClick={onLinkClick}
              prefetch="viewport"
            >
              {title}
            </AppLink>
          </HeadingTag>
          {subTitle && (
            <div className={styles.subTitle} id={descriptionId}>
              {subTitle}
            </div>
          )}
          {details && (
            <dl className={styles.details}>
              {details.map((detail) => (
                <React.Fragment key={detail.label}>
                  <dt className="sr-only">{detail.label}:</dt>
                  <dd className={styles.detailsItem}>
                    <Badge size="sm" tone="fillSecondary">
                      {detail.value}
                    </Badge>
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
        <footer className={styles.footer}>
          <div className={styles.footerMain}>
            <dl className={styles.price}>
              {mainPrice && (
                <>
                  <dt className={styles.priceLabel}>{mainPrice.label}</dt>
                  <dd className={styles.priceValue}>{mainPrice.value}</dd>
                </>
              )}
            </dl>
            {manufacturerPackaingImage && (
              <img
                src={manufacturerPackaingImage.src}
                srcSet={manufacturerPackaingImage.srcSet}
                alt={manufacturerPackaingImage.alt}
                className={styles.footerLogo}
                data-testid={`${testId}-manufacturer-packaging-logo`}
              />
            )}
          </div>
          {otherPrice && (
            <ul className={styles.otherPrices}>
              {otherPrice.map((price) => (
                <li key={price.label} className={styles.otherPrice}>
                  <span className={styles.otherPriceLabel}>{price.label}</span>
                  <span className={styles.otherPriceValue}>{price.value}</span>
                </li>
              ))}
            </ul>
          )}
        </footer>
        {floatingAction && <div className={styles.floatingAction}>{floatingAction}</div>}
      </article>
    );
  },
);

ProductCard.displayName = 'ProductCard';
